import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken = "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

const SimpleParcelMap = () => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current) return;

    // Initialize map
    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [-82.456058, 29.386663], // Center from first feature
      zoom: 12,
    });

    // Add navigation controls
    mapRef.current.addControl(new mapboxgl.NavigationControl(), "bottom-right");

    mapRef.current.on("load", async () => {
      try {
        console.log("Loading GeoJSON...");

        // Load GeoJSON file
        const response = await fetch("/assets/parcel/Fema_Flood_Parcel_Custom3.geojson");
        
        if (!response.ok) {
          throw new Error(`Failed to load: ${response.status}`);
        }

        const geojsonData = await response.json();

        console.log("Loaded features:", geojsonData.features?.length);

        // Add GeoJSON source
        mapRef.current.addSource("parcels", {
          type: "geojson",
          data: geojsonData,
        });

        // Add polygon fill layer with FEMA color coding
        mapRef.current.addLayer({
          id: "parcel-fill",
          type: "fill",
          source: "parcels",
          paint: {
            "fill-color": [
              "match",
              ["get", "parcel_custom_fema_flood"],
              "X", "#4CAF50",      // Green - Minimal Risk
              "AE", "#FF9800",     // Orange - High Risk + BFE
              "VE", "#E91E63",     // Pink - Coastal + BFE
              "A", "#2196F3",      // Blue - High Risk (No BFE)
              "#9E9E9E"            // Gray - Default/Unknown
            ],
            "fill-opacity": 0.6,
          },
        });

        // Add outline
        mapRef.current.addLayer({
          id: "parcel-outline",
          type: "line",
          source: "parcels",
          paint: {
            "line-color": "#000",
            "line-width": 0.5,
          },
        });

        // Add popup on click
        mapRef.current.on("click", "parcel-fill", (e) => {
          const props = e.features[0].properties;

          new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML(`
              <div style="padding:12px; font-size:13px; max-width:280px;">
                <h3 style="margin:0 0 8px 0; color:#1565C0; font-size:14px; font-weight:600;">
                  ${props.parcel_custom_address || "N/A"}
                </h3>
                
                <hr style="margin:8px 0; border:none; border-top:1px solid #ddd;">
                
                <p style="margin:4px 0;"><strong>City:</strong> ${props.parcel_custom_city || "N/A"}</p>
                <p style="margin:4px 0;"><strong>County:</strong> ${props.parcel_custom_county || "N/A"}</p>
                <p style="margin:4px 0;"><strong>Flood Zone:</strong> <span style="background:#FFE36B; padding:2px 6px; border-radius:3px; font-weight:600;">${props.parcel_custom_fema_flood || "N/A"}</span></p>
                <p style="margin:4px 0;"><strong>Risk Level:</strong> ${props.parcel_custom_fema_nri_r || "N/A"}</p>
                <p style="margin:4px 0;"><strong>Size:</strong> ${Number(props.parcel_custom_sqft || 0).toLocaleString()} sq ft</p>
                <p style="margin:4px 0;"><strong>Zoning:</strong> ${props.parcel_custom_zoning || "N/A"}</p>
                <p style="margin:4px 0;"><strong>Year Built:</strong> ${props.parcel_custom_yearbuilt || "N/A"}</p>
              </div>
            `)
            .addTo(mapRef.current);
        });

        // Change cursor on hover
        mapRef.current.on("mouseenter", "parcel-fill", () => {
          mapRef.current.getCanvas().style.cursor = "pointer";
        });

        mapRef.current.on("mouseleave", "parcel-fill", () => {
          mapRef.current.getCanvas().style.cursor = "";
        });

        // Fit map to show all features
        const bounds = new mapboxgl.LngLatBounds();
        
        geojsonData.features.forEach((feature) => {
          if (feature.geometry.type === "MultiPolygon") {
            feature.geometry.coordinates.forEach((polygon) => {
              polygon[0].forEach((coord) => {
                bounds.extend(coord);
              });
            });
          } else if (feature.geometry.type === "Polygon") {
            feature.geometry.coordinates[0].forEach((coord) => {
              bounds.extend(coord);
            });
          }
        });

        if (!bounds.isEmpty()) {
          mapRef.current.fitBounds(bounds, { padding: 50 });
        }

        console.log("Map loaded successfully");

      } catch (error) {
        console.error("Error loading map:", error);
      }
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);


  return (
    <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
      {/* Map Container */}
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />

      {/* Legend */}
      <div style={{
        position: "absolute",
        top: 10,
        right: 10,
        background: "rgba(255, 255, 255, 0.95)",
        padding: "15px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        fontSize: "12px",
        minWidth: "200px",
      }}>
        <div style={{ 
          fontWeight: "bold", 
          marginBottom: "12px", 
          fontSize: "14px",
          borderBottom: "2px solid #1565C0",
          paddingBottom: "6px",
        }}>
          FEMA Flood Zones
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <div style={{ width: 20, height: 20, background: "#4CAF50", borderRadius: "3px" }} />
          <span>X - Minimal Risk</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <div style={{ width: 20, height: 20, background: "#FF9800", borderRadius: "3px" }} />
          <span>AE - High Risk</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <div style={{ width: 20, height: 20, background: "#E91E63", borderRadius: "3px" }} />
          <span>VE - Coastal</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <div style={{ width: 20, height: 20, background: "#2196F3", borderRadius: "3px" }} />
          <span>A - No BFE</span>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: 20, height: 20, background: "#9E9E9E", borderRadius: "3px" }} />
          <span>Unknown</span>
        </div>
      </div>
    </div>
  );
};

export default SimpleParcelMap;

// import React, { useEffect, useRef } from "react";
// import mapboxgl from "mapbox-gl";
// import "mapbox-gl/dist/mapbox-gl.css";

// mapboxgl.accessToken = "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

// const SimpleParcelMap = () => {
//   const mapContainer = useRef(null);
//   const mapRef = useRef(null);

//   useEffect(() => {
//     if (mapRef.current) return;

//     // Initialize map
//     mapRef.current = new mapboxgl.Map({
//       container: mapContainer.current,
//       style: "mapbox://styles/mapbox/light-v11",
//       center: [-82.456058, 29.386663], // Center from first feature
//       zoom: 12,
//     });

//     // Add navigation controls
//     mapRef.current.addControl(new mapboxgl.NavigationControl(), "bottom-right");

//     mapRef.current.on("load", async () => {
//       try {
//         console.log("Loading GeoJSON...");

//         // Load GeoJSON file
//         const response = await fetch("/assets/parcel/Fema_Flood_Parcel_Custom_2.geojson");
        
//         if (!response.ok) {
//           throw new Error(`Failed to load: ${response.status}`);
//         }

//         const geojsonData = await response.json();

//         console.log("Loaded features:", geojsonData.features?.length);

//         // Add GeoJSON source
//         mapRef.current.addSource("parcels", {
//           type: "geojson",
//           data: geojsonData,
//         });

//         // Add polygon fill layer with FEMA color coding
//         mapRef.current.addLayer({
//           id: "parcel-fill",
//           type: "fill",
//           source: "parcels",
//           paint: {
//             "fill-color": [
//               "match",
//               ["get", "parcel_custom_fema_flood"],
//               "X", "#4CAF50",      // Green - Minimal Risk
//               "AE", "#FF9800",     // Orange - High Risk + BFE
//               "VE", "#E91E63",     // Pink - Coastal + BFE
//               "A", "#2196F3",      // Blue - High Risk (No BFE)
//               "#9E9E9E"            // Gray - Default/Unknown
//             ],
//             "fill-opacity": 0.6,
//           },
//         });

//         // Add outline
//         mapRef.current.addLayer({
//           id: "parcel-outline",
//           type: "line",
//           source: "parcels",
//           paint: {
//             "line-color": "#000",
//             "line-width": 0.5,
//           },
//         });

//         // Add popup on click
//         mapRef.current.on("click", "parcel-fill", (e) => {
//           const props = e.features[0].properties;

//           new mapboxgl.Popup()
//             .setLngLat(e.lngLat)
//             .setHTML(`
//               <div style="padding:12px; font-size:13px; max-width:280px;">
//                 <h3 style="margin:0 0 8px 0; color:#1565C0; font-size:14px; font-weight:600;">
//                   ${props.parcel_custom_address || "N/A"}
//                 </h3>
                
//                 <hr style="margin:8px 0; border:none; border-top:1px solid #ddd;">
                
//                 <p style="margin:4px 0;"><strong>City:</strong> ${props.parcel_custom_city || "N/A"}</p>
//                 <p style="margin:4px 0;"><strong>County:</strong> ${props.parcel_custom_county || "N/A"}</p>
//                 <p style="margin:4px 0;"><strong>Flood Zone:</strong> <span style="background:#FFE36B; padding:2px 6px; border-radius:3px; font-weight:600;">${props.parcel_custom_fema_flood || "N/A"}</span></p>
//                 <p style="margin:4px 0;"><strong>Risk Level:</strong> ${props.parcel_custom_fema_nri_r || "N/A"}</p>
//                 <p style="margin:4px 0;"><strong>Size:</strong> ${Number(props.parcel_custom_sqft || 0).toLocaleString()} sq ft</p>
//                 <p style="margin:4px 0;"><strong>Zoning:</strong> ${props.parcel_custom_zoning || "N/A"}</p>
//                 <p style="margin:4px 0;"><strong>Year Built:</strong> ${props.parcel_custom_yearbuilt || "N/A"}</p>
//               </div>
//             `)
//             .addTo(mapRef.current);
//         });

//         // Change cursor on hover
//         mapRef.current.on("mouseenter", "parcel-fill", () => {
//           mapRef.current.getCanvas().style.cursor = "pointer";
//         });

//         mapRef.current.on("mouseleave", "parcel-fill", () => {
//           mapRef.current.getCanvas().style.cursor = "";
//         });

//         // Fit map to show all features
//         const bounds = new mapboxgl.LngLatBounds();
        
//         geojsonData.features.forEach((feature) => {
//           if (feature.geometry.type === "MultiPolygon") {
//             feature.geometry.coordinates.forEach((polygon) => {
//               polygon[0].forEach((coord) => {
//                 bounds.extend(coord);
//               });
//             });
//           } else if (feature.geometry.type === "Polygon") {
//             feature.geometry.coordinates[0].forEach((coord) => {
//               bounds.extend(coord);
//             });
//           }
//         });

//         if (!bounds.isEmpty()) {
//           mapRef.current.fitBounds(bounds, { padding: 50 });
//         }

//         console.log("Map loaded successfully");

//       } catch (error) {
//         console.error("Error loading map:", error);
//       }
//     });

//     return () => {
//       if (mapRef.current) {
//         mapRef.current.remove();
//         mapRef.current = null;
//       }
//     };
//   }, []);

//   return (
//     <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
//       {/* Map Container */}
//       <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />

//       {/* Legend */}
//       <div style={{
//         position: "absolute",
//         top: 10,
//         right: 10,
//         background: "rgba(255, 255, 255, 0.95)",
//         padding: "15px",
//         borderRadius: "8px",
//         boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
//         fontSize: "12px",
//         minWidth: "200px",
//       }}>
//         <div style={{ 
//           fontWeight: "bold", 
//           marginBottom: "12px", 
//           fontSize: "14px",
//           borderBottom: "2px solid #1565C0",
//           paddingBottom: "6px",
//         }}>
//           FEMA Flood Zones
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
//           <div style={{ width: 20, height: 20, background: "#4CAF50", borderRadius: "3px" }} />
//           <span>X - Minimal Risk</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
//           <div style={{ width: 20, height: 20, background: "#FF9800", borderRadius: "3px" }} />
//           <span>AE - High Risk</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
//           <div style={{ width: 20, height: 20, background: "#E91E63", borderRadius: "3px" }} />
//           <span>VE - Coastal</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
//           <div style={{ width: 20, height: 20, background: "#2196F3", borderRadius: "3px" }} />
//           <span>A - No BFE</span>
//         </div>
        
//         <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
//           <div style={{ width: 20, height: 20, background: "#9E9E9E", borderRadius: "3px" }} />
//           <span>Unknown</span>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SimpleParcelMap;